import type { New } from "../../../../api/client/types/new.types";
import type {
  NewFormData,
  MaintenanceNewFormData,
  PoolRegulationNewFormData,
  AdverseEventNewFormData,
} from "../../components/RegisterNewForm/RegisterNewForm.types";
import {
  REPORT_TYPE_NAMES,
  MAINTENANCE_TYPE_NAMES,
  createAdverseEventTypeValue,
  createMaintenanceTypeValue,
} from "../../utils/reportTypeConstants";

/**
 * Maps an API New (full entity) to the form's initial data shape.
 * Handles report_type_id for adverse/maintenance sub-options (adverse_X, maintenance_X).
 *
 * For Mantenimiento: only "Elementos de Emergencia" appears at report type level.
 * Other maintenance types (Características del Agua, etc.) use base Mantenimiento + sub-select.
 */
export function mapNewToInitialData(
  newItem: New
): Partial<
  | NewFormData
  | MaintenanceNewFormData
  | PoolRegulationNewFormData
  | AdverseEventNewFormData
> {
  const reportTypeName = newItem.report_type?.name?.toLowerCase().trim();
  let reportTypeId = newItem.report_type?.id?.toString() ?? "";

  // Eventos Adversos with a specific adverse type: use "adverse_X" so the correct option is selected
  if (
    reportTypeName === REPORT_TYPE_NAMES.ADVERSE_EVENTS &&
    newItem.adverse_events_type?.id != null
  ) {
    reportTypeId = createAdverseEventTypeValue(newItem.adverse_events_type.id);
  }
  // Mantenimiento with maintenance type: use "maintenance_X" only for types at report level (Elementos de Emergencia)
  // Others use base Mantenimiento + maintenance_type_id sub-select
  if (
    reportTypeName === REPORT_TYPE_NAMES.MAINTENANCE &&
    newItem.maintenance_type?.id != null
  ) {
    const maintenanceTypeName = newItem.maintenance_type?.name;
    if (maintenanceTypeName === MAINTENANCE_TYPE_NAMES.EMERGENCY_ELEMENTS) {
      reportTypeId = createMaintenanceTypeValue(newItem.maintenance_type.id);
    }
    // else: keep reportTypeId as Mantenimiento id; maintenance_type_id prefills sub-select
  }

  return {
    event_timestamp: newItem.event_timestamp ?? "",
    report_type_id: reportTypeId,
    event_description: newItem.event_description ?? "",
    security_officer_name:
      (newItem as { security_officer_name?: string | null }).security_officer_name ?? "",
    maintenance_type_id: newItem.maintenance_type?.id?.toString() ?? "",
    pool_regulation_id: newItem.pool_regulation?.id?.toString() ?? "",
    intervention_level_id: newItem.intervention_level?.id?.toString() ?? "",
    adverse_events_type_id: newItem.adverse_events_type?.id?.toString() ?? "",
    spent_items: newItem.spent_items ?? null,
    // Derive Q1 from the nested main_factor so the cascade renders correctly in edit mode
    event_main_factor_id:
      newItem.event_specific_factor?.main_factor?.id?.toString() ?? undefined,
    event_specific_factor_id:
      newItem.event_specific_factor?.id?.toString() ?? undefined,
    adverse_event_intervention_level_id:
      newItem.adverse_event_intervention_level?.id?.toString() ?? undefined,
  };
}
